import React, { useState } from "react";

// A realtime date-range. ISO 8601 strings; `to: null` means "now" (the backend
// defaults datetime_to to now), `from: null` means "whole history".
export interface DateRange {
  from: string | null;
  to: string | null;
}

const PRESETS: { key: string; label: string; hours: number | null }[] = [
  { key: "6h", label: "6h", hours: 6 },
  { key: "24h", label: "24h", hours: 24 },
  { key: "48h", label: "48h", hours: 48 },
  { key: "7d", label: "7d", hours: 24 * 7 },
  { key: "all", label: "All", hours: null },
];

// The default range a dashboard should fetch on first load (matches PRESETS.24h).
export const DEFAULT_RANGE: DateRange = {
  from: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
  to: null,
};

// ISO -> value for <input type="datetime-local"> in the user's local timezone.
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

const DateRangePicker: React.FC<{
  defaultPreset?: string;
  onChange: (r: DateRange) => void;
}> = ({ defaultPreset = "24h", onChange }) => {
  const [preset, setPreset] = useState<string>(defaultPreset);
  const [showCustom, setShowCustom] = useState(false);
  const [fromInput, setFromInput] = useState<string>(toLocalInput(DEFAULT_RANGE.from));
  const [toInput, setToInput] = useState<string>(""); // blank = now

  const pickPreset = (key: string) => {
    setPreset(key);
    setShowCustom(false);
    const p = PRESETS.find((x) => x.key === key)!;
    const from = p.hours == null ? null : new Date(Date.now() - p.hours * 3600 * 1000).toISOString();
    onChange({ from, to: null });
  };

  const applyCustom = () => {
    const from = fromInput ? new Date(fromInput).toISOString() : null;
    const to = toInput ? new Date(toInput).toISOString() : null;
    setPreset("custom");
    onChange({ from, to });
  };

  const btn = (active: boolean) =>
    `rounded-md px-3 py-1 text-xs font-medium transition-colors ${
      active ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
    }`;

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="inline-flex flex-wrap items-center gap-0.5 rounded-lg border border-paper-300 p-0.5">
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => pickPreset(p.key)} className={btn(preset === p.key)}>
            {p.label}
          </button>
        ))}
        <button onClick={() => setShowCustom((s) => !s)} className={btn(preset === "custom")}>
          Custom
        </button>
      </div>
      {showCustom && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-paper-300/70 bg-surface p-3 shadow-soft">
          <label className="flex flex-col text-[11px] font-medium text-ink-muted">
            From
            <input
              type="datetime-local"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className="mt-0.5 rounded border border-paper-300 px-2 py-1 text-sm text-ink"
            />
          </label>
          <label className="flex flex-col text-[11px] font-medium text-ink-muted">
            To <span className="text-ink-faint">· blank = now</span>
            <input
              type="datetime-local"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              className="mt-0.5 rounded border border-paper-300 px-2 py-1 text-sm text-ink"
            />
          </label>
          <button
            onClick={applyCustom}
            className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-on-brand hover:bg-brand-600"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
